import { useState, type ReactNode } from 'react';
import type { Team, WorkspaceProjection } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { Popover } from '@localagent/ui/components/popover';
import { Tooltip } from '@localagent/ui/components/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import { cn } from '@localagent/ui/lib/utils';
import { WorkspaceReadiness } from './WorkspaceReadiness';
import { WorkspaceArchiveTeamsNotice } from './WorkspaceArchiveTeamsNotice';
import { PageHeaderBar } from '../layout/PageHeaderBar';
import { BackButton } from '../layout/BackButton';
import { filesystemStatusLabel, workstationStatusLabel } from './workspaceDisplay';

function shortenHome(path: string): string {
  return path.replace(/^\/(Users|home)\/[^/]+(?=\/|$)/, '~');
}

function statusDetail(workspace: WorkspaceProjection, isLaunched: boolean, showAdvanced: boolean): string {
  const parts = [
    `Computer ${workstationStatusLabel(workspace.workstation.status)}`,
    `Folder ${filesystemStatusLabel(workspace)}`,
  ];
  if (isLaunched) parts.push('Launched');
  if (showAdvanced) parts.push(`Lifecycle ${workspace.lifecycle}`);
  return parts.join(' · ');
}

function LocationIcon({ local }: { local: boolean }) {
  return local ? (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M2 20h20" />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.75" />
      <circle cx="12" cy="12" r="1.75" />
      <circle cx="19" cy="12" r="1.75" />
    </svg>
  );
}

const MENU_ITEM =
  'flex w-full items-center rounded px-2 py-1.5 text-left text-body hover:bg-accent focus:bg-accent focus:outline-none disabled:cursor-not-allowed disabled:opacity-50';

export function WorkspaceDetailHeader({
  workspace,
  showAdvancedChrome,
  isLaunched,
  attaching,
  archivePending,
  teams,
  teamsError,
  tabs,
  onBack,
  onOpenInChat,
  onLaunch,
  onDismiss,
  onArchive,
}: {
  workspace: WorkspaceProjection;
  showAdvancedChrome: boolean;
  isLaunched: boolean;
  attaching: boolean;
  archivePending: boolean;
  teams: Team[] | undefined;
  teamsError: Error | null;
  tabs: ReactNode;
  onBack: () => void;
  onOpenInChat: () => void;
  onLaunch: () => void;
  onDismiss: () => void;
  onArchive: (callbacks: { onSuccess: () => void; onError: (message: string) => void }) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [archiveFailure, setArchiveFailure] = useState<string | null>(null);

  const local = workspace.assignment.routing_class === 'local';
  const folder = workspace.binding?.folder_path ?? '';
  const archive = workspace.actions.archive;

  return (
    <>
      <PageHeaderBar
        testId="workspace-detail-header"
        leading={
          <>
            <BackButton label="Back to workspaces" testId="workspace-detail-back" onClick={onBack} />
          </>
        }
        title={<span title={workspace.name}>{workspace.name}</span>}
        adornment={
          <>
      <Tooltip label={statusDetail(workspace, isLaunched, showAdvancedChrome)} side="bottom" multiline>
        <span className="inline-flex shrink-0" tabIndex={0} data-testid="workspace-detail-status">
          <WorkspaceReadiness projection={workspace} />
        </span>
      </Tooltip>

      <span
        className="hidden min-w-0 shrink items-center gap-1.5 text-caption text-muted-foreground md:inline-flex"
        title={folder || (local ? 'On this computer' : 'In the cloud')}
        data-testid="workspace-detail-location"
      >
        <LocationIcon local={local} />
        <span className="truncate font-mono">{folder ? shortenHome(folder) : local ? 'This computer' : 'Cloud'}</span>
      </span>

          </>
        }
        tabs={tabs}
        actions={
          <>
      <div className="flex shrink-0 items-center gap-1">
        <Button size="sm" data-testid="workspace-detail-open-in-chat" disabled={attaching} onClick={onOpenInChat}>
          {attaching ? 'Opening…' : 'Open in chat'}
        </Button>
        <Popover
          align="end"
          portal
          open={menuOpen}
          onOpenChange={setMenuOpen}
          aria-label="More workspace actions"
          triggerClassName="h-8 w-8 text-muted-foreground hover:bg-accent hover:text-foreground"
          contentClassName="w-56 p-1"
          trigger={<span data-testid="workspace-detail-more"><MoreIcon /></span>}
        >
          <div role="menu" className="flex flex-col">
            {isLaunched ? (
              <button
                type="button"
                role="menuitem"
                className={MENU_ITEM}
                data-testid="workspace-detail-dismiss"
                onClick={() => {
                  setMenuOpen(false);
                  onDismiss();
                }}
              >
                Dismiss
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                className={MENU_ITEM}
                data-testid="workspace-detail-launch"
                onClick={() => {
                  setMenuOpen(false);
                  onLaunch();
                }}
              >
                Launch
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className={cn(MENU_ITEM, 'text-destructive')}
              data-testid="workspace-detail-delete"
              disabled={!archive.available}
              onClick={() => {
                setMenuOpen(false);
                setArchiveFailure(null);
                setConfirmingArchive(true);
              }}
            >
              Archive…
            </button>
            {!archive.available && (
              <p className="px-2 pb-1.5 text-caption text-muted-foreground" data-testid="workspace-detail-archive-refusal">
                {archive.refusal.message}
              </p>
            )}
          </div>
        </Popover>
      </div>

          </>
        }
      />

      <Dialog
        open={confirmingArchive}
        onOpenChange={(next) => {
          if (!next && !archivePending) setConfirmingArchive(false);
        }}
      >
        <DialogContent className="max-w-md" data-testid="workspace-archive-dialog">
          <DialogHeader>
            <DialogTitle>Archive {workspace.name}?</DialogTitle>
            <DialogDescription>
              It leaves your workspaces list and agents can no longer use it.
            </DialogDescription>
          </DialogHeader>
          <WorkspaceArchiveTeamsNotice teams={teams} error={teamsError} />
          {archiveFailure && (
            <p className="text-body text-destructive" role="alert" data-testid="workspace-detail-archive-api-refusal">
              {archiveFailure}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={archivePending} onClick={() => setConfirmingArchive(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={archivePending || teams === undefined || teamsError !== null}
              data-testid={`confirm-delete-${workspace.workspace_id}`}
              onClick={() => {
                setArchiveFailure(null);
                onArchive({
                  onSuccess: () => setConfirmingArchive(false),
                  onError: (message) => setArchiveFailure(message),
                });
              }}
            >
              {archivePending ? 'Archiving…' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
